import { Ed25519, Secp256k1 } from "open-signer-shared-cryptography";
import type { Environment } from "./api";
import { CrossmintApiService, getEnvironment } from "./api";
import type { AttestationService } from "./attestation";
import { AttestationDevService, TEEAttestationService } from "./attestation";
import { EventsService } from "./communications/events";
import { CryptoKeyService } from "./crypto/crypto-key";
import { EncryptionService } from "./encryption";
import type { FrameLogger } from "./logger";
import {
	DeviceCache,
	type FrameStorageAdapter,
	IndexedDBAdapter,
	InMemoryStorageAdapter,
	isEphemeralDeviceStorageRequested,
} from "./storage";
import { DeviceService } from "./user/device";
import { SecretService } from "./user/secret";

/**
 * Services index - Export all services
 */
export { initializeHandlers } from "./communications/handlers";
export { CrossmintFrameService } from "./service";

export type CrossmintFrameServices = {
	events: EventsService;
	api: CrossmintApiService;
	encrypt: EncryptionService;
	attestation: AttestationService;
	cryptoKey: CryptoKeyService;
	deviceCache: DeviceCache;
	device: DeviceService;
	storage: FrameStorageAdapter;
	secret: SecretService;
};

const STAGING_PHALA_APP_IDS = [
	"df4f0ec61f92a8eec754593da9ea9cd939985e9c",
	"14b63b54690ea796fb80779ff9a66f73a623fbd9",
];
const PRODUCTION_PHALA_APP_ID = "ff01a45fc0b4721d8054001be79e68e88ffd13e3";

/**
 * Creates an attestation service based on the environment
 */
function createAttestationService(
	env: Environment,
	crossmintApiService: CrossmintApiService,
	logger: FrameLogger,
): AttestationService {
	switch (env) {
		case "development":
			return new AttestationDevService(crossmintApiService, logger);
		case "staging":
			return new TEEAttestationService(
				crossmintApiService,
				STAGING_PHALA_APP_IDS,
				logger,
			);
		case "production":
			return new TEEAttestationService(
				crossmintApiService,
				PRODUCTION_PHALA_APP_ID,
				logger,
			);
		default:
			throw new Error(`Unknown environment: ${env}`);
	}
}

export function createCrossmintFrameServices(
	logger: FrameLogger,
): CrossmintFrameServices {
	const usesEphemeralStorage = isEphemeralDeviceStorageRequested();
	if (usesEphemeralStorage) {
		logger.info(
			"[Services] Ephemeral device storage requested - using in-memory storage, IndexedDB disabled",
		);
	}
	const storageService = usesEphemeralStorage
		? new InMemoryStorageAdapter(logger)
		: new IndexedDBAdapter(logger);
	const eventsService = new EventsService(logger);
	const deviceService = new DeviceService(storageService, logger);

	const crossmintApiService = new CrossmintApiService(logger);

	const attestationService = createAttestationService(
		getEnvironment(),
		crossmintApiService,
		logger,
	);

	const encryptionService = new EncryptionService(
		logger,
		attestationService,
		deviceService,
	);
	crossmintApiService.setEncryptionService(encryptionService);

	const deviceCache = new DeviceCache(crossmintApiService, logger);

	const cryptoKeyService = new CryptoKeyService(
		logger,
		new Ed25519(),
		new Secp256k1(),
	);

	const secretService = new SecretService(
		deviceCache,
		encryptionService,
		crossmintApiService,
		logger,
	);

	return {
		storage: storageService,
		events: eventsService,
		attestation: attestationService,
		encrypt: encryptionService,
		deviceCache: deviceCache,
		api: crossmintApiService,
		cryptoKey: cryptoKeyService,
		device: deviceService,
		secret: secretService,
	};
}
